"use client";
import { useModalContext } from "@/app/context/QuickViewModalContext";
import { updateQuickView } from "@/redux/features/quickView-slice";
import {
  addItemToWishlist,
  removeItemFromWishlist,
} from "@/redux/features/wishlist-slice";
import { AppDispatch, useAppSelector } from "@/redux/store";
import { imageBuilder } from "@/sanity/vendure-utils";
import { Product } from "@/types/product";
import Image from "next/image";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useShoppingCart } from "use-shopping-cart";
import { useAutoOpenCart } from "../Providers/AutoOpenCartProvider";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { formatPrice } from "@/utils/formatePrice";
import { formatPromotionBadgeLabel } from "@/utils/promotions";

const SingleListItem = ({
  item,
  host,
}: {
  item: Product;
  host: string | null;
}) => {
  const { openModal } = useModalContext();
  const dispatch = useDispatch<AppDispatch>();

  const { cartDetails } = useShoppingCart();
  const { addItemWithAutoOpen } = useAutoOpenCart();
  const wishlistItems = useAppSelector((state) => state.wishlistReducer.items);
  const t = useTranslations("Shop");

  const isItemInCart = Object.values(cartDetails ?? {}).some(
    (cartItem) => cartItem.id.toString() === item._id.toString(),
  );

  const isItemInWishlist = Object.values(wishlistItems ?? {}).some(
    (wishlistItem) => wishlistItem._id.toString() === item._id.toString(),
  );

  const cartItem = {
    id: item._id,
    name: item.name,
    price: item.price,
    currency: "usd",
    image: item?.previewImages
      ? imageBuilder(item?.previewImages[0]?.image).url()
      : "",
    price_id: null,
    slug: item?.slug?.current,
  };

  // update the QuickView state
  const handleQuickViewUpdate = () => {
    dispatch(updateQuickView({ ...item, host }));
  };

  // add to cart
  const handleAddToCart = () => {
    // @ts-ignore
    addItemWithAutoOpen(cartItem);
    toast.success("Product added to cart!");
  };

  const handleToggleWishList = () => {
    if (isItemInWishlist) {
      dispatch(removeItemFromWishlist(item._id));
      toast.success("Product removed from wishlist!");
    } else {
      dispatch(
        addItemToWishlist({
          ...item,
          quantity: 1,
        }),
      );
      toast.success("Product added to wishlist!");
    }
  };

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="bg-white rounded-lg group shadow-1">
      <div className="flex">
        <div className="shadow-list relative overflow-hidden flex items-center justify-center max-w-[270px] w-full sm:min-h-[270px] p-4">
          <button
            onClick={() => {
              openModal();
              handleQuickViewUpdate();
            }}
            aria-label="button for quick view"
            className="cursor-pointer"
          >
            <Image
              src={imageBuilder(item?.previewImages[0]?.image).url() || ""}
              alt={item.name}
              className="w-full h-full object-cover"
              width={270}
              height={270}
            />
          </button>

          {((item.badges && item.badges.length > 0) || item.activePromotion) && (
            <div className="absolute top-2 left-2 flex flex-col items-start gap-1 pointer-events-none z-10">
              {item.activePromotion && (
                <span className="text-white text-[0.6rem] font-bold tracking-widest uppercase px-2.5 py-1 rounded bg-red">
                  {formatPromotionBadgeLabel(item.activePromotion)}
                </span>
              )}
              {item.badges?.map((badge) => (
                <span
                  key={badge.id}
                  className="text-white text-[0.6rem] font-bold tracking-widest uppercase px-2.5 py-1 rounded"
                  style={{ backgroundColor: badge.color }}
                >
                  {badge.name}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="w-full flex flex-col gap-5 sm:flex-row sm:items-center justify-center sm:justify-between py-5 px-4 sm:px-7.5 lg:pl-11 lg:pr-12">
          <div>
            <h3 className="font-medium text-dark ease-out duration-200 hover:text-blue mb-1.5">
              {item.name}
            </h3>

            <span className="flex items-center gap-2 text-lg font-medium">
              {item.activePromotion ? (
                <>
                  <span className="text-dark-4 line-through text-base font-normal">
                    {item.hasPriceVariants ? "od " : ""}
                    {formatPrice(item.price)}
                  </span>
                  <span className="text-red">
                    {item.hasPriceVariants ? "od " : ""}
                    {formatPrice(item.discountedPrice)}
                  </span>
                </>
              ) : (
                <span className="text-dark">
                  {item.hasPriceVariants ? "od " : ""}
                  {formatPrice(item.price)}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleListItem;
